import { forwardRef } from 'react';

import { cn } from '../../utils/cn';
import type { PressableProps } from './Pressable.types';

// Pressable accepts a forwarded ref without binding to React's `Ref<T>`
// generic. pnpm hoists `@types/react` into multiple paths (one per workspace
// + worktree); TypeScript treats them as distinct nominal types even when
// the versions match, which makes `Ref<HTMLElement>` from shared not
// assignable to `Ref<HTMLElement>` from flashka-web. A structural callback /
// MutableRefObject shape sidesteps that — runtime is unchanged.
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * Web Pressable — renders `<button>` by default. Use `as="a"` for links
 * (combine with `webProps={{ href: ... }}`) or `as="div"` for non-semantic
 * pressable areas.
 *
 * Baseline classes reset all browser button chrome so that the same className
 * produces the same visual result as react-native's Pressable on mobile.
 * Focus-visible ring + cursor + disabled opacity are web-specific niceties
 * layered on top.
 *
 * `nativeProps` and most accessibility-aria-name props are ignored on web —
 * `accessibilityLabel` maps to `aria-label`, `accessibilityRole` maps to `role`.
 */
const RESET_CLASSES =
  'appearance-none m-0 p-0 border-0 bg-transparent font-inherit text-inherit text-left ' +
  'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background';

function PressableInner(
  {
    as = 'button',
    className,
    children,
    disabled,
    accessibilityLabel,
    accessibilityRole,
    testID,
    onPress,
    onPressIn,
    onPressOut,
    onLongPress,
    onHoverIn,
    onHoverOut,
    webProps,
    // intentionally swallowed on web:
    nativeProps: _nativeProps,
    // Haptics are native-only (see `triggerHaptic`). Swallow the prop here so
    // it never reaches `injectedRest` and lands as an unknown DOM attribute.
    haptic: _haptic,
    // Press-scale is native-only too — web gets its pressed scale from the
    // `enabled:active:scale-*` classes. Swallow for the same reason.
    pressScale: _pressScale,
    // Slot-style consumers (Radix `Trigger asChild`, react-router `Link`,
    // auth buttons, etc.) clone Pressable and attach extra props
    // — pointer / focus / blur handlers, aria-*, data-* — at the top
    // level. Non-conflicting keys (aria-*, data-*, onPointerDown, onFocus,
    // …) flow through via `injectedRest` below. Conflicting mouse handlers
    // (onClick, onMouseDown/Up, onContextMenu, onMouseEnter/Leave) are
    // composed with our own `onPress`-derived handlers so Radix's trigger
    // `onClick` and the caller's `onPress` both fire.
    ...injected
  }: PressableProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const merged = cn(RESET_CLASSES, className);
  const ariaLabel = accessibilityLabel ?? webProps?.['aria-label'];
  const role = accessibilityRole ?? webProps?.role;

  // Compose two handlers so neither silently overrides the other. Slot-style
  // consumers (Radix `Trigger asChild`) inject their own `onClick` to open
  // the popover / menu, while local callers wire `onPress` for analytics or
  // app logic. Both must fire — running `injected` first matches the order
  // Radix's own composeEventHandlers uses, and respects `preventDefault()`
  // from the injected handler.
  type MouseHandler = (e: React.MouseEvent<HTMLElement>) => void;
  const compose = (
    injectedHandler: MouseHandler | undefined,
    ours: MouseHandler | undefined,
  ): MouseHandler | undefined => {
    if (!injectedHandler) return ours;
    if (!ours) return injectedHandler;
    return (e: React.MouseEvent<HTMLElement>) => {
      injectedHandler(e);
      if (!e.defaultPrevented) ours(e);
    };
  };

  const injectedHandlers = injected as {
    onClick?: MouseHandler;
    onMouseDown?: MouseHandler;
    onMouseUp?: MouseHandler;
    onContextMenu?: MouseHandler;
    onMouseEnter?: MouseHandler;
    onMouseLeave?: MouseHandler;
  };
  // Extract the conflicting handlers out of `injected` so the
  // `{...injected}` spread below doesn't re-add the raw injected version
  // alongside the composed one. Remaining `injected` keys (aria-*, data-*,
  // onPointerDown, onFocus, …) flow through untouched.
  const {
    onClick: _injectedClick,
    onMouseDown: _injectedMouseDown,
    onMouseUp: _injectedMouseUp,
    onContextMenu: _injectedContextMenu,
    onMouseEnter: _injectedMouseEnter,
    onMouseLeave: _injectedMouseLeave,
    ...injectedRest
  } = injectedHandlers;

  // Every handler receives the full React.MouseEvent<HTMLElement>, which
  // matches PressEvent exactly — DOM target, modifier keys, coordinates,
  // and propagation control are all available downstream.
  // onLongPress is approximated with onContextMenu (right-click / long-press
  // on touch); we preventDefault to suppress the native context menu before
  // forwarding the event.
  // `webProps?.onX` types narrow to the intersection of all three element
  // shapes (HTMLButtonElement & HTMLAnchorElement & HTMLDivElement) which
  // is stricter than our `MouseEvent<HTMLElement>` handler signature.
  // Cast through `unknown` once at the boundary so the composition utility
  // sees uniform `MouseHandler | undefined` for both branches.
  const wp = webProps as unknown as
    | {
        onClick?: MouseHandler;
        onMouseDown?: MouseHandler;
        onMouseUp?: MouseHandler;
        onContextMenu?: MouseHandler;
        onMouseEnter?: MouseHandler;
        onMouseLeave?: MouseHandler;
      }
    | undefined;

  const sharedHandlers = {
    onClick: compose(
      injectedHandlers.onClick,
      onPress
        ? (e: React.MouseEvent<HTMLElement>) => {
            if (!disabled) onPress(e);
          }
        : wp?.onClick,
    ),
    onMouseDown: compose(
      injectedHandlers.onMouseDown,
      onPressIn ? (e: React.MouseEvent<HTMLElement>) => onPressIn(e) : wp?.onMouseDown,
    ),
    onMouseUp: compose(
      injectedHandlers.onMouseUp,
      onPressOut ? (e: React.MouseEvent<HTMLElement>) => onPressOut(e) : wp?.onMouseUp,
    ),
    onContextMenu: compose(
      injectedHandlers.onContextMenu,
      onLongPress
        ? (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            onLongPress(e);
          }
        : wp?.onContextMenu,
    ),
    onMouseEnter: compose(
      injectedHandlers.onMouseEnter,
      onHoverIn ? (e: React.MouseEvent<HTMLElement>) => onHoverIn(e) : wp?.onMouseEnter,
    ),
    onMouseLeave: compose(
      injectedHandlers.onMouseLeave,
      onHoverOut || onPressOut
        ? (e: React.MouseEvent<HTMLElement>) => {
            onHoverOut?.(e);
            onPressOut?.(e);
          }
        : wp?.onMouseLeave,
    ),
  };

  if (as === 'a') {
    return (
      <a
        {...(injectedRest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        {...(webProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        ref={ref as unknown as React.Ref<HTMLAnchorElement>}
        className={merged}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        role={role}
        data-testid={testID}
        {...sharedHandlers}>
        {children}
      </a>
    );
  }

  if (as === 'div') {
    // Keyboard activation — the div branch only. It renders role="button"
    // with tabIndex=0, but a <div>, unlike a real <button>, does NOT
    // synthesize a click from Enter/Space: without this every DS card built
    // on it (PractiseCard & co.) is mouse-only — focusable, focus-ringed, and
    // completely inert to keyboard and screen-reader users. `<button>` and
    // `<a href>` already do this natively, so they're left alone.
    // Space is preventDefault'd so the page doesn't scroll under the card.
    type KeyHandler = (e: React.KeyboardEvent<HTMLElement>) => void;
    const consumerKeyDown = [
      (injectedRest as { onKeyDown?: KeyHandler }).onKeyDown,
      (webProps as unknown as { onKeyDown?: KeyHandler } | undefined)?.onKeyDown,
    ];
    // Placed AFTER both spreads so it wins the JSX last-prop-wins race; the
    // consumer handlers it displaced are invoked first, and a
    // `preventDefault()` from either suppresses our activation.
    const onKeyDown: KeyHandler = (e) => {
      for (const handler of consumerKeyDown) handler?.(e);
      if (disabled || e.defaultPrevented) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      // Only when the div ITSELF is focused. These cards nest real controls
      // (PractiseCard's ⋯ actions button, checkboxes) whose keydown bubbles
      // here — the actions slots stop `click` propagation but not `keydown`,
      // so without this guard activating the inner control would ALSO open
      // the card. A real <button> can't have this problem: its activation is
      // native to the element, not a bubbling listener.
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      // PressEvent is typed as a MouseEvent (the web element is always
      // clicked in practice); the members press handlers actually use —
      // target, currentTarget, stopPropagation, preventDefault — are
      // identical on a KeyboardEvent.
      onPress?.(e as unknown as React.MouseEvent<HTMLElement>);
    };
    return (
      <div
        {...(injectedRest as React.HTMLAttributes<HTMLDivElement>)}
        {...(webProps as React.HTMLAttributes<HTMLDivElement>)}
        ref={ref as unknown as React.Ref<HTMLDivElement>}
        className={merged}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        role={role ?? 'button'}
        tabIndex={disabled ? -1 : 0}
        data-testid={testID}
        {...sharedHandlers}
        onKeyDown={onKeyDown}>
        {children}
      </div>
    );
  }

  return (
    <button
      // `type="button"` is the default so a bare <Pressable> inside a <form>
      // doesn't accidentally submit it. Consumers (and Radix Slot `asChild`
      // triggers) can override via `webProps={{ type: 'submit' }}` or by
      // injecting `type` through the Slot, because both spreads come after
      // this default — the LAST prop wins in JSX.
      type="button"
      {...(injectedRest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      {...(webProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      ref={ref as unknown as React.Ref<HTMLButtonElement>}
      className={merged}
      aria-label={ariaLabel}
      role={role}
      data-testid={testID}
      disabled={disabled}
      {...sharedHandlers}>
      {children}
    </button>
  );
}

/**
 * Web Pressable as a `forwardRef` so libraries that use `asChild` semantics
 * (Radix's Tooltip / Popover / Dialog triggers, react-router's <Link>) can
 * measure and bind events to the underlying HTML element. `as="a"` returns
 * an `HTMLAnchorElement` and `as="div"` returns an `HTMLDivElement`, but
 * the consumer-facing ref type stays as the cross-platform `HTMLElement` —
 * cast at the receiving end if you need the more specific type.
 */
// Cast through `unknown` to detach from React's `Ref<T>` generic. See
// `AnyRef` above for the rationale (multi-copy `@types/react` paths under
// pnpm). Runtime behavior is identical to a regular `forwardRef`.
export const Pressable = forwardRef(
  PressableInner as never,
) as unknown as React.ForwardRefExoticComponent<
  PressableProps & { ref?: AnyRef<HTMLElement> }
>;
(Pressable as { displayName?: string }).displayName = 'Pressable';
